import * as v from "valibot";

import type {
  UpdatePlayerDataMessage,
  AckUpdatePlayerDataMessage,
} from "@textclub/common/bridge";
import {
  applyStateUpdate,
  containsNulCharacter,
} from "@textclub/common/player-state";
import type { PlayerDataUpdate } from "@textclub/common/types";
import { UnreachableError } from "@textclub/common/utils";

import { instrumentSdkMethod } from "../instrumentation";
import { Record, parseArgs } from "../schema-utils";
import type { ModuleContext } from "./module-context";

const GetPlayerDataValArgs = v.tuple([v.string()]);
const SetPlayerDataArgs = v.tuple([
  v.pipe(
    Record,
    v.check(
      (value) => !containsNulCharacter(value),
      "player data cannot contain the NUL character (\\u0000)",
    ),
  ),
]);

/**
 * The DataModule manages all player data state and operations.
 * It owns the player data state and provides both:
 * - Public instrumented methods (emit "data.*" analytics) for the new API
 * - Core non-instrumented methods for deprecated method delegation
 */
export class DataModule {
  private playerData: Record<string, unknown> = {};
  private updateCounter = 0;
  private pendingUpdates: Array<PlayerDataUpdate & { updateId: number }> = [];
  private unackedUpdates: Array<UpdatePlayerDataMessage> = [];
  private pendingFlushPromises: Array<{ id: number; resolve: () => void }> = [];
  private initialized = false;

  constructor(private ctx: ModuleContext) {
    this.ctx.registerMessageHandlers({
      AckUpdatePlayerData: (message) => this.handleAckMessage(message),
    });
  }

  // Exposed module interface
  @instrumentSdkMethod({ name: "data.get" })
  get(key: string): unknown {
    return this._coreGet(key);
  }

  @instrumentSdkMethod({ name: "data.getAll" })
  getAll(): Record<string, unknown> {
    return this._coreGetAll();
  }

  @instrumentSdkMethod({ name: "data.set" })
  set(keyOrPartial: string | Record<string, unknown>, value?: unknown): void {
    if (typeof keyOrPartial === "string") {
      return this._coreSet({ [keyOrPartial]: value });
    }
    return this._coreSet(keyOrPartial);
  }

  @instrumentSdkMethod({ name: "data.delete" })
  delete(key: string): void {
    return this._coreSet({ [key]: undefined });
  }

  @instrumentSdkMethod({ name: "data.flush" })
  flush(): Promise<void> {
    return this._coreFlush();
  }

  /** Exposed for decorator use - delegates to context */
  instrumentMethod<T>(opts: { method: string; args: unknown; fn: () => T }): T {
    return this.ctx.instrumentMethod(opts);
  }

  nextUpdateId(): number {
    return this.updateCounter + 1;
  }

  /**
   * Called by Sdk.initPlayer() to set up initial data from stored player data.
   */
  initialize(storedData: Record<string, unknown>): void {
    this.playerData = { ...storedData };
    this.initialized = true;
  }

  /**
   * Called by the SDK when AckUpdatePlayerData message is received.
   */
  handleAckMessage(message: AckUpdatePlayerDataMessage): void {
    if (this.unackedUpdates.length > 1) {
      // this is currently an unexpected case, update pushing is currently synchronous
      // so we should never have more than one unacked update
      throw new Error("Unexpected multiple unacked updates");
    }
    if (message.id !== this.unackedUpdates[0]?.id) {
      throw new Error("Unexpected ack id");
    }
    this.unackedUpdates.shift();

    // resolve any pending flush promises that have an id less than or equal to the ack id
    for (const { id, resolve } of this.pendingFlushPromises) {
      if (id <= message.id) {
        resolve();
      }
    }

    this.flushUpdates();
  }

  // === Core Non-Instrumented Methods (for deprecated method delegation) ===

  /**
   * Core implementation of get - no instrumentation.
   */
  _coreGet(key: string): unknown {
    this.ensureInitialized();

    parseArgs(GetPlayerDataValArgs, [key]);

    return this.playerData[key];
  }

  /**
   * Core implementation of getAll - no instrumentation.
   */
  _coreGetAll(): Record<string, unknown> {
    this.ensureInitialized();
    return { ...this.playerData };
  }

  /**
   * Core implementation of set - no instrumentation.
   */
  _coreSet(partial: Record<string, unknown>): void {
    this.ensureInitialized();

    parseArgs(SetPlayerDataArgs, [partial]);

    this.updateCounter++;
    const update = { update: partial };
    this.playerData = applyStateUpdate(this.playerData, update);
    this.pendingUpdates.push({
      update: update.update,
      updateId: this.updateCounter,
    });
    this.flushUpdates();
  }

  /**
   * Core implementation of flush - no instrumentation.
   */
  _coreFlush(): Promise<void> {
    if (this.pendingUpdates.length === 0 && this.unackedUpdates.length === 0) {
      return Promise.resolve();
    }
    const flushPromise = new Promise<void>((resolve) => {
      this.pendingFlushPromises.push({ id: this.updateCounter, resolve });
    });
    return flushPromise;
  }

  private ensureInitialized(): void {
    if (!this.initialized) {
      throw new Error(
        "Player not initialized. Await `JestSDK.init()` before doing anything.",
      );
    }
  }

  /**
   * Sends any updates that haven't been acknowledged by the host.
   */
  private flushUpdates(): void {
    // reduce pending updates into a single update
    if (this.unackedUpdates.length > 0) {
      return;
    }

    if (this.pendingUpdates.length === 0) {
      return;
    }

    const maxUpdateId = this.pendingUpdates[this.pendingUpdates.length - 1];
    if (maxUpdateId === undefined) {
      throw new UnreachableError("maxUpdateId should be defined");
    }

    // collapse any pending updates into a single message
    const collapsedPlayerDataUpdate = this.pendingUpdates.reduce((acc, cur) => {
      return { ...acc, ...cur.update };
    }, {});

    const updateMessage: UpdatePlayerDataMessage = {
      type: "UpdatePlayerData",
      id: maxUpdateId.updateId,
      update: { update: collapsedPlayerDataUpdate },
    };

    // send the updates
    this.unackedUpdates.push(updateMessage);
    this.pendingUpdates = [];
    this.ctx.sendMessage(updateMessage);
  }
}
